import {useEffect, useMemo, useRef, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {
    FinalCommitteeDecisionLookup,
    OrderLocalResult,
    ProgramOrderLocalResult
} from "services/orders/models/OrdersTypes";
import {useTranslation} from "react-i18next";
import {useShallow} from "zustand/react/shallow";
import GridView from "components/GridView";
import useGridStore from "components/GridView/grid.store";
import {OrdersService} from "services/orders";
import {useNotification} from "hooks/useNotification";
import {GridSchema, GridViewHeaderProps} from "components/GridView/GridView.types";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import useFileDownload from "hooks/useFileDownload";
import {useBreadcrumb} from "contexts/breadcrumb/BreadcrumbContext";
import {useCookieContext} from "contexts";
import {LoginResult} from "services/users/models/loginResult";
import {prepareFilters} from "components/GridView/components/GridFilter";
import {GzipProvider} from "../../../helpers/compress/gzipProvider";
import {OrderStatus} from "../../../shared/lookups";
import AuthGuard from "../../../components/blocks/AuthGuard";
import IconButton from "../../../components/blocks/IconButton";
import ActionModal from "../../../components/blocks/Modal/ActionModal";
import { useTitle } from "hooks/useTitle";

type OrderGridViewColumns =
    | "id"
    | "programName"
    | "appliedUserName"
    | "researcherClassification"
    | "createdOn"
    | "centreManagementDetail"
    | "orderPhase"
    | "centreManagerName"
    | "orderStatus";

export default function OrderGridView({
                                          userId,
                                          columnsToHide,
                                          programId,
                                      }: {
    userId?: string;
    columnsToHide?: OrderGridViewColumns[];
    programId?: number;
}) {
    const isRequestLoading = useRef(false);
    const {setBreadcrumbs} = useBreadcrumb();
    const {t, i18n} = useTranslation(["Orders"]);
    const {toSystemThemeDateFormat} = useSystemDateTheme();
    const {showNotification} = useNotification();
    const downloadFile = useFileDownload();
    const {isAr, userSession} = useCookieContext();
    const location = useLocation();

    const ready = useRef(false);
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState<OrderLocalResult[] | undefined>();
    const [totalCount, setTotalCount] = useState(0);
    // central management orders
    const isCentralManagerOrders = location.pathname.includes("centralManagerOrders");

    const {sortState, pagination, setPagination, searchValue, filters, resetGridStore} = useGridStore(
        useShallow((state) => ({
            sortState: state.sortState,
            pagination: state.pagination,
            setPagination: state.setPagination,
            searchValue: state.generalSearch,
            filters: state.filters,
            resetGridStore: state.reset,
        })),
    );

    const [modalProps, setModalProps] = useState({
        isOpen: false,
        type: "cancel",
        orderNumber: "",
        orderId: 0,
        loading: false,
    });
    const fetchOrders = async ({resetPagination = false}: { resetPagination?: boolean } = {}) => {
        if (isRequestLoading.current) {
            return;
        }

        if (isCentralManagerOrders && !userSession) {
            return;
        }

        try {
            isRequestLoading.current = true;
            setLoading(true);
            const response = await OrdersService.getAllOrdersAsync({
                pageIndex: resetPagination ? 1 : pagination.currentPage,
                pageSize: 10,
                search: searchValue,
                sort: sortState
                    ? Object.keys(sortState).map((key) => ({
                        field: key,
                        dir: sortState[key],
                    }))
                    : [],
                filter: [
                    ...prepareFilters(filters),
                    ...(programId
                        ? [
                            {
                                field: "ProgramId",
                                operator: "equal",
                                value: programId,
                            },
                        ]
                        : []),
                    ...(isCentralManagerOrders && userSession
                        ? [
                            {
                                field: "CentreManagement.CentreManagerId",
                                operator: "equal",
                                value: userSession!.id,
                            },
                        ]
                        : []),
                    ...(userId
                        ? [
                            {
                                field: "AppliedUserId",
                                operator: "equal",
                                value: userId,
                            },
                        ]
                        : []),
                ],
            });

            setOrders(response.data.result);
            setTotalCount(response.data.totalCount);

            if (resetPagination) {
                setPagination({
                    currentPage: 1,
                    totalPages: Math.ceil(response.data.totalCount / 10),
                });
            }

            isRequestLoading.current = false;
        } catch (error) {
            showNotification({
                type: "error",
                description: t("Common:internalServerError"),
            });
            setPagination({
                currentPage: 1,
                totalPages: 0,
            });
        } finally {
            setLoading(false);
        }
    };


    const cancelOrderConfirmed = async () => {
        try {
            setModalProps({...modalProps, loading: true});
            await OrdersService.CancelOrder(modalProps.orderId);
            showNotification({
                type: "success",
                description: t("orderCanceledSuccessfully"),
            });

            fetchOrders({resetPagination: true});
        } catch (error) {
            showNotification({
                type: "error",
                description: t("Common:internalServerError"),
            });
        } finally {
            setModalProps({
                isOpen: false,
                type: "cancel",
                orderNumber: "",
                orderId: 0,
                loading: false,
            });
        }
    };

    useEffect(() => {
        if (!ready.current) {
            return;
        }

        fetchOrders({resetPagination: true});
    }, [sortState, searchValue, filters, isAr, userSession]);

    useEffect(() => {
        if (!ready.current) {
            return;
        }

        fetchOrders();
    }, [pagination.currentPage]);

    useEffect(() => {
        if (!ready.current) {
            resetGridStore();
            ready.current = true;
            return;
        }
    }, [resetGridStore]);

    useEffect(() => {
        setBreadcrumbs([{localizationKey: "allOrders", path: "/home/orders", menuItemId: "allOrders"}]);
    }, []);

    const gridSchema = useMemo<GridSchema[]>(
        () => [
            {
                accessor: "id",
                field: "id",
                displayName: t("orderNumber"),
                sortable: true,
                render: (row: OrderLocalResult) => {
                    return <Link to={`/home/orders/view/${row.id}`}>{row.serialNumber}</Link>;
                },
                showOnMobile: true,
            },
            {
                field: "ProgramId",
                displayName: t("programName"),
                sortable: true,
                filterable: false,
                isHidden: columnsToHide?.includes("programName"),
                showOnMobile: true,
                render(row: OrderLocalResult) {
                    return <>{row.programDetail.displayName}</>;
                },
            },
            {
                accessor: "appliedUserName",
                field: "AppliedUserId",
                displayName: t("applicantName"),
                isHidden: columnsToHide?.includes("appliedUserName"),
                sortable: true,
                filterable: true,
                filterOptions: {
                    type: "serverSideSelect",
                    serverSideSelectProps: {
                        label: t("applicantName"),
                        service: "accountService",
                        endpoint: "getUsersDropdown",
                        idValueKey: "id",
                        isPaginated: true,
                        textValueKey: isAr ? "arabicName" : "englishName",
                        useReactSelect: true,
                    },
                },
                render(row) {
                    return <span>{isAr ? row.appliedUser?.arabicName : row.appliedUser?.englishName}</span>;
                },
            },
            {
                accessor: "researcherClassification",
                field: "finalCommitteeDecision",
                displayName: t("researcherClassification"),
                sortable: true,
                filterable: true,
                filterOptions: {
                    type: "select",
                    options: [
                        {
                            id: FinalCommitteeDecisionLookup.Primary.id,
                            value: t("Primary"),
                        },
                        {
                            id: FinalCommitteeDecisionLookup.Secondary.id,
                            value: t("Secondary"),
                        },
                    ],
                },
                render(row: OrderLocalResult) {
                    return <>{t(row.finalCommitteeDecision || "-")}</>;
                },
            },
            {
                field: "createdOn",
                displayName: t("submissionDate"),
                sortable: true,
                filterable: true,
                filterOptions: {
                    type: "date",
                },
                render(row: OrderLocalResult) {
                    return <>{toSystemThemeDateFormat(row.createdOn)}</>;
                },
            },
            {
                field: "CentreManagementId",
                displayName: t("researchesCenter"),
                sortable: true,
                filterable: true,
                render(row: OrderLocalResult) {
                    return <>{row.centreManagementDetail.displayName}</>;
                },
                filterOptions: {
                    type: "serverSideSelect",
                    serverSideSelectProps: {
                        label: t("researchesCenter"),
                        service: "orderService",
                        endpoint: "getManagementCentersDropdown",
                        textValueKey: "centreManagementDetail.displayName",
                        idValueKey: "id",
                        isPaginated: true,
                        useReactSelect: true,
                    },
                },
            },
            {
                accessor: "orderPhase",
                field: "OrderPhase",
                displayName: t("orderStage"),
                sortable: true,
                filterable: true,
            },
            {
                accessor: "centreManagerName",
                field: `CentreManager.${isAr ? "ArabicName" : "EnglishName"}`,
                displayName: t("responsibleParty"),
                sortable: false,
                filterable: false,
                render(row) {
                    return <span>{isAr ? row.centreManager?.arabicName : row.centreManager?.englishName}</span>;
                },
            },
            {
                field: "OrderStatusId",
                displayName: t("orderStatus"),
                sortable: true,
                filterable: false,
                filterOptions: {
                    type: "select",
                    options: [
                        {id: "1", value: t("active")},
                        {id: "0", value: t("inactive")},
                    ],
                },
                render(row: OrderLocalResult) {
                    // TODO: color based on status (primary, success, danger)
                    const statusClassName =
                        row.orderStatus === "Completed" ? "bg-success text-success " : "bg-primary text-primary";
                    return <span
                        className={`badge bg-opacity-10  rounded-4 ${statusClassName} py-2`}>{t(row.orderStatus)}</span>;
                },
            },

            {
                field: "actions",
                displayName: t("Common:actions"),
                showOnMobile: true,
                render: (row: ProgramOrderLocalResult) => {
                    return row.orderStatusId === OrderStatus.Draft ||
                    row.orderStatusId === OrderStatus.Unacceptable ||
                    row.orderStatusId === OrderStatus.Completed ? null : (
                        <div className={"d-flex gap-1 "}>
                            <AuthGuard requiredRoles={["FundingAllOrdersDelete"]}>
                                <IconButton
                                    icon="icon-block"
                                    onClick={() => {
                                        console.log(row);
                                        setModalProps({
                                            isOpen: true,
                                            type: modalProps.type,
                                            orderNumber: row.serialNumber ??"",
                                            orderId: row.id,
                                            loading: false,
                                        });
                                    }}
                                    size="sm"
                                    title={t("Common:cancel")}
                                    // className="btn action-btn btn-sm rounded p-2"
                                    variant="light"
                                    innerIconColor="danger"
                                    fitToIconWidth
                                />
                            </AuthGuard>
                        </div>
                    );
                },
            },
        ],

        [columnsToHide, t, toSystemThemeDateFormat, isAr],
    );

    const viewHeader: GridViewHeaderProps = {
        title: isCentralManagerOrders ? t("Shared:centralManagerOrders") : t("allOrders"),
        singularName: t("order"),
        addPageRoute: "/home/orders/add",
        totalCount: totalCount,
        hideAddButton: true,
        onExportClick: async () => {
            setLoading(true);
            const response = await OrdersService.exportToExcel({
                pageIndex: pagination.currentPage,
                pageSize: 10,
                search: searchValue,
            });
            await downloadFile(response, "orders.xlsx");
            setLoading(false);
        },
    };

    return (
        <div className="bg-white rounded-4 h-100">
            <GridView
                loading={loading}
                gridProps={{
                    data: orders ?? [],
                    gridSchema,
                }}
                viewHeaderProps={viewHeader}
            />

            <ActionModal
                isOpen={modalProps.isOpen}
                onClose={() => {
                    setModalProps({
                        isOpen: false,
                        type: "cancel",
                        orderNumber: "",
                        orderId: 0,
                        loading: false,
                    });
                }}
                headerMsg={t("Common:areYouSure")}
                subMsg={t("cancelOrderConfirmationMsg", {
                    orderNumber: modalProps.orderNumber,
                })}
                actionIcon="icon-block"
                confirmBtnText={t("cancelOrder")}
                iconWrapperColor={'warning'}
                onActionConfirm={cancelOrderConfirmed}
                loading={modalProps.loading}
            />
        </div>
    );
}
